import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL } from '../utils/Function';
import '../Css/ServicesDetails.css';
import Topnavbar from '../Header-pages/Topnavbar';
import Footer from '../Header-pages/Footer';
import Navbar from '../Header-pages/Navbar';
import { Helmet } from "react-helmet-async";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft,  
  faDollarSign, 
  faCheck, 
  faCheckCircle, 
  faChevronLeft, 
  faChevronRight, 
  faPause, 
  faPlay,
  faBuilding,
  faRocket,
  faStar
} from '@fortawesome/free-solid-svg-icons';
import { faClock as farClock } from '@fortawesome/free-regular-svg-icons';
import { faReact } from '@fortawesome/free-brands-svg-icons';

const ServicesDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [relatedProjects, setRelatedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Gallery slider state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [galleryImages, setGalleryImages] = useState([]);

  useEffect(() => {
    const fetchServiceDetails = async () => {
        
      try {
        setLoading(true);
        // Fetch all services and find by slug
        const response = await fetch(`${API_URL}/service.php/get`);
        const data = await response.json();
        
        if (data.success) {
          const foundService = data.services.find(s => s.slug === slug);
          if (foundService) {
            // Fetch specific service details by ID
            const detailResponse = await fetch(`${API_URL}/service.php/get/${foundService.id}`);
            const detailData = await detailResponse.json();
            
            if (detailData.success) {
              setService(detailData.service);
              
              // Set gallery images
              if (detailData.service.gallery_images_urls && detailData.service.gallery_images_urls.length > 0) {
                setGalleryImages(detailData.service.gallery_images_urls);
              } else if (detailData.service.featured_image_url) {
                // Fallback to featured image if no gallery images
                setGalleryImages([detailData.service.featured_image_url]);
              }
              
              // Fetch related projects if project_id exists
              if (detailData.service.project_id) {
                const projectResponse = await fetch(`${API_URL}/project.php/get`);
                const projectData = await projectResponse.json();
                if (projectData.success) {
                  setRelatedProjects(projectData.projects.slice(0, 3));
                }
              }
            } else {
              setError('Service not found');
            }
          } else {
            setError('Service not found');
          }
        } else {
          setError(data.error || 'Failed to fetch service');
        }
      } catch (err) {
        setError('Network error: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchServiceDetails();
    window.scrollTo(0, 0);
  }, [slug]);

  // Auto slide effect
  useEffect(() => {
    if (!isAutoPlaying || galleryImages.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === galleryImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // Change image every 5 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying, galleryImages.length]);

  // Navigation functions
  const nextImage = () => {
    setCurrentImageIndex(currentImageIndex === galleryImages.length - 1 ? 0 : currentImageIndex + 1);
  };

  const prevImage = () => {
    setCurrentImageIndex(currentImageIndex === 0 ? galleryImages.length - 1 : currentImageIndex - 1);
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  const toggleAutoPlay = () => {
    setIsAutoPlaying(!isAutoPlaying);
  };

  if (loading) {
    return (
      <div className="service-details-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>Loading service details...</p>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="service-details-error">
        <div className="alert alert-danger">
          {error || 'Service not found'}
        </div>
        <button onClick={() => navigate('/')} className="btn btn-primary">
          Go Back Home
        </button>
      </div>
    );
  }
  

  return (
    <>
     <Helmet>
        <title>
          {service?.meta_title 
            ? service.meta_title
            : `${service?.title} | SAFPRO Technology Solutions`}
        </title>

        <meta 
          name="description" 
          content={service?.meta_description || service?.short_description || service?.title} 
        />
      </Helmet>
    <Topnavbar />
    <Navbar />
    <div className="service-details-page">
      {/* Hero Section */}
      <section className="service-hero">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="breadcrumb-nav">
                <FontAwesomeIcon icon={faArrowLeft} />
                <span onClick={() => navigate('/')} style={{cursor: 'pointer'}}>
                  Web Development
                </span>
              </div>
              <h1 className="service-hero-title">{service.title}</h1>
              {service.tagline && (
                <p className="service-hero-tagline">{service.tagline}</p>
              )}
              <p className="service-hero-description">{service.short_description || service.description}</p>

              <div className="hero-actions">
                <button className="btn btn-primary btn-lg">Start Your Project</button>
                <button className="btn btn-outline-primary btn-lg">View Portfolio</button>
              </div>
            </div>
            <div className="col-lg-6">
              {/* Gallery Slider */}
              {galleryImages.length > 0 && (
                <div className="gallery-slider">
                  <div className="slider-container">
                    {/* Main Image Display */}
                    <div className="slider-image-wrapper">
                      <img 
                        src={`${API_URL}/${galleryImages[currentImageIndex]}`}
                        alt={`${service.title} - Image ${currentImageIndex + 1}`}
                        className="slider-image"
                      />
                      
                      {/* Navigation Arrows */}
                      {galleryImages.length > 1 && (
                        <>
                          <button 
                            className="slider-arrow slider-arrow-left"
                            onClick={prevImage}
                          >
                            <FontAwesomeIcon icon={faChevronLeft} />
                          </button>
                          <button 
                            className="slider-arrow slider-arrow-right"
                            onClick={nextImage}
                          >
                            <FontAwesomeIcon icon={faChevronRight} />
                          </button>
                        </>
                      )}

                      {/* Image Counter */}
                      {galleryImages.length > 1 && (
                        <div className="slider-counter">
                          {currentImageIndex + 1} / {galleryImages.length}
                        </div>
                      )}

                      {/* Auto-play Toggle */}
                      {galleryImages.length > 1 && (
                        <button 
                          className="autoplay-toggle"
                          onClick={toggleAutoPlay}
                        >
                          <FontAwesomeIcon icon={isAutoPlaying ? faPause : faPlay} />
                        </button>
                      )}

                      {/* Dots Navigation */}
                      {galleryImages.length > 1 && (
                        <div className="slider-dots">
                          {galleryImages.map((_, index) => (
                            <button
                              key={index}
                              className={`slider-dot ${index === currentImageIndex ? 'active' : ''}`}
                              onClick={() => goToImage(index)}
                            />
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Thumbnail Preview */}
                    {galleryImages.length > 1 && (
                      <div className="slider-thumbnails">
                        {galleryImages.map((image, index) => (
                          <div
                            key={index}
                            className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                            onClick={() => goToImage(index)}
                          >
                            <img src={`${API_URL}/${image}`} alt={`Thumbnail ${index + 1}`} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Service Overview */}
      <section className="service-overview">
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <h2 className="overview-section-heading">Service Overview</h2>
              <div className="overview-description-text" dangerouslySetInnerHTML={{ __html: service.description }} />

              {/* Key Features */}
              {service.features_array && service.features_array.length > 0 && (
                <div className="key-features-wrapper">
                  <h3 className="key-features-title">Key Features</h3>
                  <div className="key-features-grid-container">
                    {service.features_array.map((feature, index) => (
                      <div key={index} className="key-feature-item-box">
                        <div className="key-feature-icon-wrapper">
                          <FontAwesomeIcon icon={feature.icon ? getIconFromString(feature.icon) : faCheckCircle} />
                        </div>
                        <div className="key-feature-text-content">
                          <h4 className="key-feature-heading">{feature.title || feature}</h4>
                          {feature.description && <p className="key-feature-description">{feature.description}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Benefits Section */}
              {service.benefits_array && service.benefits_array.length > 0 && (
                <div className="benefits-wrapper">
                  <h3 className="benefits-title">Benefits You'll Get</h3>
                  <div className="benefits-grid-container">
                    {service.benefits_array.map((benefit, index) => (
                      <div key={index} className="benefit-item-box">
                        <div className="benefit-icon-wrapper">
                          <FontAwesomeIcon icon={benefit.icon ? getIconFromString(benefit.icon) : faStar} />
                        </div>
                        <div className="benefit-text-content">
                          <h4 className="benefit-heading">{benefit.title || benefit}</h4>
                          {benefit.description && <p className="benefit-description">{benefit.description}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="col-lg-4">
              <div className="service-sidebar">
                <div className="sidebar-card info-card">
                  {service.service_category && (
                    <div className="category-section">
                      <h4 className="category-label">Service Category</h4>
                      <div className="category-list">
                        {service.service_category.split(',').map((category, index) => (
                          <div key={index} className="category-item">
                            <FontAwesomeIcon icon={faCheckCircle} className="category-icon" />
                            <span>{category.trim()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="contact-section">
                    <p className="contact-text">
                      For more details about this service, pricing, and custom requirements, please contact us.
                    </p>
                    
                    <a onClick={()=>navigate('/Contactus')} className="btn btn-primary btn-block" style={{cursor:'pointer'}}>Contact Us</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Development Process - Updated to horizontal layout */}
      {service.process_steps_array && service.process_steps_array.length > 0 && (
        <section className="development-process">
          <div className="container">
            <h2 className="section-title text-center">Our Development Process</h2>
            <p className="section-description text-center">
              A proven methodology that ensures successful project delivery from concept to launch
            </p>
            
            <div className="process-horizontal">
              {service.process_steps_array.map((step, index) => (
                <div key={index} className="process-step-horizontal">
                  <div className="step-number-horizontal">{index + 1}</div>
                  <div className="step-content-horizontal">
                    <h4>{step.title || step}</h4>
                    {step.description && <p>{step.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Technologies - Updated to show only names */}
      {service.technologies_array && service.technologies_array.length > 0 && (
        <section className="technologies-section">
          <div className="container">
            <h2 className="section-title text-center">Technologies We Use</h2>
            <p className="section-description text-center">
              Modern, proven technologies for robust web applications
            </p>
            
            <div className="technologies-names">
              {service.technologies_array.map((tech, index) => (
                <span key={index} className="tech-name-item">
                  {tech.name || tech}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Projects */}
      {relatedProjects.length > 0 && (
        <section className="related-projects">
          <div className="container">
            <h2 className="section-title text-center">Related Projects</h2>
            <p className="section-description text-center">
              Successful web development projects in this category
            </p>
            
            <div className="row">
              {relatedProjects.map((project) => (
                <div key={project.id} className="col-lg-4 col-md-6 mb-4">
                  <div className="project-card">
                    {project.featured_image_url && (
                      <div className="project-image">
                        <img src={project.featured_image_url} alt={project.title} />
                        <div className="project-overlay">
                          <span className="project-category">{project.category}</span>
                          <span className="project-tech">{project.technologies}</span>
                          <span className="project-learn">Learn More</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Target Audience & Deliverables */}
      <section className="audience-deliverables">
        <div className="container">
          <div className="row">
            {service.target_industries_array && service.target_industries_array.length > 0 && (
              <div className="col-lg-6">
                <h2 className="section-title">Target Audience</h2>
                <div className="audience-list">
                  {service.target_industries_array.map((industry, index) => (
                    <div key={index} className="audience-item">
                      <FontAwesomeIcon icon={faBuilding} />
                      <div>
                        <h4>{industry.name || industry}</h4>
                        {industry.description && <p>{industry.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {service.deliverables_array && service.deliverables_array.length > 0 && (
              <div className="col-lg-6">
                <h2 className="section-title">What You'll Receive</h2>
                <ul className="deliverables-list">
                  {service.deliverables_array.map((item, index) => (
                    <li key={index}>
                      <FontAwesomeIcon icon={faCheckCircle} />
                      {item.title || item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
    <Footer />
    </>
  );
};

// Helper function to convert string icon names to FontAwesome icons
const getIconFromString = (iconString) => {
  const iconMap = {
    'fa-check-circle': faCheckCircle,
    'fa-building': faBuilding,
    'fa-clock': farClock,
    'fa-dollar-sign': faDollarSign,
    'fa-react': faReact,
    'fa-check': faCheck,
    'fa-rocket': faRocket,
    'fa-star': faStar,
    // Add more mappings as needed
  };
  
  return iconMap[iconString] || faCheckCircle; // Default fallback
};

export default ServicesDetails;